import React, { useEffect, useState, useRef } from 'react';
import {
  ListingFilter,
  ListingFilterOption,
  PlanListingFilterOptionsListProps,
  PlanListingFilterProps,
} from '../Types/Plans.Type';
import { Text, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { keyboardClick, useDictionary } from 'lib/utils';
import Icon from 'components/helpers/Icon';
import classNames from 'classnames/bind';
import styles from './PlanOptionsListingGraphQL.module.scss';
import { updateFilterCounts } from './PlanListing.Functions';
import Button from 'components/helpers/Button/Button';
import PlanListingSorting from './PlanListing.Sorting';
import Modal from 'components/helpers/Modal';

const cx = classNames.bind(styles);

const PlanListingFilter: React.FC<PlanListingFilterProps> = (props) => {
  const {
    plans,
    filters,
    displayType,
    sortingOptions,
    defaultSelection,
    updatePlansSortOrder,
    onFilterOptionSelectionChange,
  } = props;

  const isMobile = displayType === 'mobile';

  const $closeLabel = useDictionary('Close');
  const filtersAndSort = useDictionary('FilterAndSort', 'Filter & Sort');
  const filtersLabel = useDictionary('Filters');
  let mainFiltersLabel = filtersLabel;

  if (isMobile) {
    mainFiltersLabel = filtersAndSort;
  }

  const clearAllFiltersLabel = useDictionary('Clear all filters');
  const clearAllLabel = useDictionary('Clear All');
  const showResultsLabel = useDictionary('Show Results');

  const [filtersWithCount, setFiltersWithCount] = useState<ListingFilter[]>([]);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // <dialog> only on mobile
  const dialogRef = useRef<HTMLDialogElement | null>(null);

  const onFiltersClick = () => {
    if (isMobile) {
      setShowMobileFilters((prev) => !prev);
    }
  };

  const selectedFiltersCount = () => {
    let $count = 0;
    if (isMobile) {
      for (const filter of filtersWithCount) {
        $count += filter.options.filter((option) => option.selected).length;
      }
    }
    return $count;
  };

  const hasSelectedFilters = () => filtersWithCount.some((filter) => filter.options.some((option) => option.selected));

  const onClearFilters = () => {
    const newFilters = filtersWithCount.map((filter) => ({
      ...filter,
      options: filter.options.map((option) => ({
        ...option,
        selected: false,
      })),
    }));

    setFiltersWithCount(newFilters);
    onFilterOptionSelectionChange?.(newFilters);
  };

  const onFilterOptionChange = (filterOption: ListingFilterOption) => {
    const updatedFilters = filtersWithCount.map((filter) => {
      if (filter.options.find((o) => o.id === filterOption.id)) {
        return {
          ...filter,
          options: filter.options.map((option) =>
            option.id === filterOption.id ? { ...option, selected: !option.selected } : option
          ),
        };
      }
      return filter;
    });

    setFiltersWithCount(updatedFilters);
    onFilterOptionSelectionChange?.(updatedFilters);
  };

  useEffect(() => {
    if (filters.length && plans.length) {
      if (filtersWithCount.length === 0) {
        const tempFilters: ListingFilter[] = [];
        updateFilterCounts(filters, plans).forEach((fltr) => {
          const newFilter: ListingFilter = {
            id: fltr.id,
            displayName: fltr.displayName,
            options: [],
            selectMultiple: fltr.selectMultiple,
            setDefault: fltr.setDefault,
            tooltip: fltr.tooltip,
            tooltipLabel: fltr.tooltipLabel,
          };
          fltr.options.forEach((option) => {
            if (option.count != undefined && option.count > 0) {
              newFilter.options.push(option);
            }
          });
          if (fltr.options.length > 0) tempFilters.push(newFilter);
        });
        setFiltersWithCount(tempFilters);
      } else {
        setFiltersWithCount(updateFilterCounts(filtersWithCount, plans));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, plans]);

  // Open/close the native dialog ONLY on mobile
  useEffect(() => {
    if (!isMobile) return;

    const dialog = dialogRef.current;
    if (!dialog) return;

    if (showMobileFilters) {
      // showModal gives you a backdrop + focus management on mobile
      if (!dialog.open) dialog.showModal();
    } else {
      if (dialog.open) dialog.close();
    }
  }, [isMobile, showMobileFilters]);

  if (!filtersWithCount?.length) {
    return null;
  }

  // Extracted panel content reused by both desktop and mobile
  const FiltersPanelContent = (
    <>
      {/* Close header (mobile only, and only when open) */}
      {isMobile && showMobileFilters && (
        <div className={cx('plan-listing-filters__filter--close')}>
          <div className={cx('horizontal-line')} onClick={onFiltersClick} aria-label={$closeLabel} />
          <button className={cx('closeIcon')} onClick={onFiltersClick} aria-label={$closeLabel} title={$closeLabel}>
            <Icon variant="close" />
          </button>
        </div>
      )}

      <div
        className={cx({
          'plan-listing-filters__filter--options-container-active-content': isMobile && showMobileFilters,
        })}
      >
        {isMobile && (
          <>
            {sortingOptions && updatePlansSortOrder && (
              <PlanListingSorting
                sortingOptions={sortingOptions}
                updatePlansSortOrder={updatePlansSortOrder}
                defaultSelection={defaultSelection}
                displayType="mobile"
              />
            )}
            <div className={cx('plan-listing-filters__heading-mobile')}>
              <Icon variant="filter" />
              {filtersLabel}
            </div>
          </>
        )}

        {filtersWithCount?.map((filter, key) => (
          <PlanListingFilterOptionsList
            filter={filter}
            isMobile={isMobile}
            key={`filter-${displayType}-${key}`}
            onFilterOptionChange={onFilterOptionChange}
            addDivider={!isMobile || key < filtersWithCount.length - 1}
          />
        ))}

        {/* Desktop "Clear all filters" */}
        {!isMobile && hasSelectedFilters() && (
          <div className={cx('plan-listing-filters__filter--clearall')}>
            <button type="button" onClick={onClearFilters} className={cx('clearFiltersPadding')}>
              {clearAllFiltersLabel}
            </button>

            <div className={cx('plan-listing-filters__filter--divider')}></div>
          </div>
        )}
      </div>

      {/* Mobile footer buttons */}
      {isMobile && (
        <div className={cx({ 'plan-listing-filters__filter--options-container-active-footer': showMobileFilters })}>
          <div className={cx('plan-listing-filters__filter--clearallmobile')}>
            <Button
              onClick={onClearFilters}
              className={cx('button-clear-filters')}
              disabled={!hasSelectedFilters()}
              aria-label={clearAllLabel}
              title={clearAllLabel}
            >
              {clearAllLabel}
            </Button>

            <Button
              onClick={onFiltersClick}
              className={cx('button-show-results')}
              aria-label={showResultsLabel}
              title={showResultsLabel}
            >
              {showResultsLabel}
            </Button>
          </div>
        </div>
      )}
    </>
  );

  return (
    <section className={cx('plan-listing-filters', `display-type-${displayType}`)}>
      {isMobile ? (
        // eslint-disable-next-line jsx-a11y/role-supports-aria-props
        <button
          id="filters-heading"
          type="button"
          className={cx('plan-listing-filters__heading', {
            'plan-listing-filters__heading-active': hasSelectedFilters(),
          })}
          onClick={onFiltersClick}
          aria-expanded={Boolean(showMobileFilters)}
          role="heading"
          aria-level={3}
        >
          <Icon variant="filter" />
          {mainFiltersLabel}
          {hasSelectedFilters() && <span> ({selectedFiltersCount()})</span>}
        </button>
      ) : (
        <h3
          id="filters-heading"
          className={cx('plan-listing-filters__heading', {
            'plan-listing-filters__heading-active': isMobile && hasSelectedFilters(),
          })}
        >
          <Icon variant="filter" />
          {mainFiltersLabel}
          {isMobile && hasSelectedFilters() && <span> ({selectedFiltersCount()})</span>}
        </h3>
      )}

      {/* Mobile uses <dialog>, Desktop uses inline <aside> */}
      {isMobile ? (
        <dialog
          ref={dialogRef}
          className={cx('plan-listing-filters__filter--options-container', {
            'plan-listing-filters__filter--options-container-active': showMobileFilters,
          })}
          aria-labelledby="filters-heading"
          // Sync state if dialog closes itself (Esc)
          onClose={() => setShowMobileFilters(false)}
          onCancel={() => setShowMobileFilters(false)}
        >
          {FiltersPanelContent}
        </dialog>
      ) : (
        <aside className={cx('plan-listing-filters__filter--options-container')} aria-labelledby="filters-heading">
          {FiltersPanelContent}
        </aside>
      )}
    </section>
  );
};

const PlanListingFilterOptionsList: React.FC<PlanListingFilterOptionsListProps> = (props) => {
  const { filter, addDivider, isMobile, onFilterOptionChange } = props;
  const { sitecoreContext } = useSitecoreContext();
  const { options } = filter;

  const contentRef = useRef<HTMLDivElement | null>(null);
  const viewMoreButtonRef = useRef<HTMLButtonElement | null>(null);

  const filtersToShowOnLoad = 5;

  const viewMore = useDictionary('View More');
  const viewLess = useDictionary('View Less');
  const showViewMore = options.length > filtersToShowOnLoad;

  const isSelected = (optionId: string) => options.find((o) => o.id === optionId)?.selected ?? false;
  const [viewAllOptions, setViewAllOptions] = useState(false);
  const [filtersToShow, setFiltersToShow] = useState(filtersToShowOnLoad);

  const onViewMoreClick = () => {
    const nextCount = !viewAllOptions ? options.length : filtersToShowOnLoad;
    setViewAllOptions(!viewAllOptions);
    setFiltersToShow(nextCount);
  };

  // Move focus when expanded → to the first newly revealed checkbox
  useEffect(() => {
    if (viewAllOptions && contentRef.current) {
      const startIndex = filtersToShowOnLoad;

      // Prefer the first enabled checkbox that just became visible (>= startIndex)
      const allEnabled = Array.from(
        contentRef.current.querySelectorAll<HTMLInputElement>('input[type="checkbox"]:not([disabled])')
      );

      const target =
        allEnabled.find((el) => {
          const idxAttr = el.getAttribute('data-index');
          const idx = idxAttr ? Number(idxAttr) : -1;
          return idx >= startIndex;
        }) || allEnabled[0];

      if (target) {
        target.focus();
      } else {
        // Fallback: focus the container itself
        (contentRef.current as HTMLElement).focus();
      }
    }
    // When collapsing (viewAllOptions === false) → do nothing; focus stays on the button
  }, [viewAllOptions, filtersToShow, options]);

  if (options?.length < 2) {
    return <></>;
  }

  return (
    <div id={filter.id} className={cx('plan-listing-filters__filter--container')}>
      <fieldset
        className={cx('plan-listing-filters__filter--fieldset')}
        style={{ border: 'none', padding: 0, margin: 0 }}
      >
        <legend className={cx('plan-listing-filters__filter--heading')}>
          <Text field={{ value: filter.displayName }} encode={sitecoreContext.pageEditing} />
        </legend>

        <div
          ref={contentRef}
          className={cx('plan-listing-filters__filter--options')}
          tabIndex={-1} // makes container focusable as fallback
        >
          {options?.slice(0, filtersToShow).map((option, key) => (
            <label
              key={`${option.id}-${key}`}
              className={cx({ disabled: !option.count })}
              style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}
            >
              <input
                type="checkbox"
                id={option.id}
                name={option.id}
                data-index={key} // enables precise focus targeting
                aria-label={`${option.displayName} (${option.count})`}
                checked={(option.count || 0) > 0 && isSelected(option.id)}
                disabled={!option.count}
                onChange={() => onFilterOptionChange(option)}
                style={{ marginRight: '8px', marginTop: '-2px' }}
              />

              <span>
                <Text field={{ value: option.displayName }} encode={sitecoreContext.pageEditing} /> ({option.count})
              </span>
            </label>
          ))}

          {showViewMore && (
            <button
              ref={viewMoreButtonRef}
              type="button"
              className={cx('view-more-topics')}
              onClick={onViewMoreClick}
              onKeyDown={(e) => keyboardClick(e, onViewMoreClick)}
              tabIndex={0}
              aria-controls={filter.id}
              aria-expanded={viewAllOptions}
            >
              {viewAllOptions ? viewLess : viewMore}
              <Icon variant={viewAllOptions ? 'minus' : 'plus'} />
            </button>
          )}
        </div>
      </fieldset>

      {filter.tooltipLabel && filter.tooltip && (
        <dialog className={cx('plan-listing-filters__filter--tooltip')} open>
          <Modal
            title={filter.tooltipLabel}
            launcherText={filter.tooltipLabel}
            isMobile={isMobile}
            tabIndex={0}
            openWithReturn={true}
            openWithSpacebar={true}
            showFooter={true}
            closeOnClick={true}
            closeOnEsc={true}
          >
            <div dangerouslySetInnerHTML={{ __html: filter.tooltip }} />
          </Modal>
        </dialog>
      )}

      {addDivider && <div className={cx('plan-listing-filters__filter--divider')} />}
    </div>
  );
};

export default PlanListingFilter;
